import type { BreadcrumbStructuredData } from "#layers/core/types/seo/breadcrumb"
import type { ListItemStructuredData } from "#layers/core/types/seo/common"
import type { OrganizationStructuredData } from "~/types/seo/organization"

export type BreadcrumbType = {
    title: string,
    /** The relative url of the breadcrumb. Optional */
    url?: string,
}

/**
 * Utility function that generates the Structured Data for the Breadcrumbs.
 * @see [breadcrumb docs](https://developers.google.com/search/docs/appearance/structured-data/breadcrumb)
 */
export const generateBreadcrumbStructuredData = (breadcrumbs: BreadcrumbType[], siteUrl: string): BreadcrumbStructuredData => {

    const itemListElement = breadcrumbs.map((breadcrumb, index) => {
        const listItem: ListItemStructuredData<string> = {
            "@type": "ListItem",
            position: index + 1,
            name: breadcrumb.title,
        }

        if (breadcrumb.url) {
            try {
                const url = new URL(siteUrl);
                url.pathname = breadcrumb.url;
                listItem.item = url.toString();
            } catch (error) {
                console.error("[ERROR]: Absolute URL cannot be composed, fallback to manual", error);
                listItem.item = `${siteUrl}${breadcrumb.url}`
            }
        }

        return listItem;
    })

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement,
    }
}

/**
 * Utility function that generates an
 * [Organization](https://developers.google.com/search/docs/appearance/structured-data/organization) structure data.
 */
export const generateOrganizationStructuredData = (organizationDetails: Omit<OrganizationStructuredData, "@context" | "@type">): OrganizationStructuredData => {
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        ...organizationDetails
    }
}